import { useState, useEffect, useRef, Fragment } from 'react'
import api from '../services/api'
import Apolices, { tiposEndosso, formatMoney, EtiquetaCombinado, textoEndossoSemApolice, montarItensEndossoFrota } from './Apolices'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import { useNavegacaoLista } from '../hooks/useNavegacaoLista'
import IconExcluir from '../components/icons/IconExcluir'
import IconAcoes from '../components/icons/IconAcoes'
import IconVisualizar from '../components/icons/IconVisualizar'
import SelecaoMultipla from '../components/SelecaoMultipla'
import { conferirDadosCliente } from '../components/ConferirCliente'

const ABAS = [
  { key: 'pendente', label: 'Propostas Pendentes', cor: 'var(--primary)' },
  { key: 'emitido',  label: 'Emitidas',            cor: 'var(--primary)' },
  { key: 'enviado',  label: 'Enviadas',            cor: 'var(--primary)' },
]

const fmtDate = (d) => d ? d.split('T')[0].split('-').reverse().join('/') : '—'

const normalizarChave = valor => String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '')
const soDigitos = valor => String(valor || '').replace(/\D/g, '')
const parseValor = valor => {
  if (typeof valor === 'number') return valor
  const texto = String(valor || '').trim()
  if (!texto) return 0
  return Number(texto.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.')) || 0
}
const somarDias = (data, dias) => {
  if (!data) return ''
  const valor = new Date(`${String(data).slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(valor.getTime())) return ''
  valor.setUTCDate(valor.getUTCDate() + dias)
  return valor.toISOString().slice(0, 10)
}

const diasDesde = (d) => {
  if (!d) return null
  return Math.floor((new Date() - new Date(d)) / (1000 * 60 * 60 * 24))
}

const colDias         = { key: 'dias',           label: 'Dias',             get: p => diasDesde(p.data_proposta),              numeric: true }
const colNome         = { key: 'nome',           label: 'Nome',             get: p => p.cliente_nome || '' }
const colCpf          = { key: 'cpf',            label: 'CPF/CNPJ',        get: p => p.cliente_cpf_cnpj || '', centralizada: true }
const colRamo         = { key: 'ramo',           label: 'Ramo',             get: p => p.ramo || '', centralizada: true }
const colSeguradora   = { key: 'seguradora',     label: 'Seguradora',      get: p => p.seguradora || '', centralizada: true }
const colNumero       = { key: 'numero',         label: 'Nº da\nProposta',  get: p => p.numero_proposta || '' }
const colNumeroApolice = { key: 'numero_apolice', label: 'Nº da\nApólice',  get: p => p.numero_apolice || p.emissao_numero_apolice || '' }
const colNumeroEndosso = { key: 'numero_endosso', label: 'Nº do\nEndosso',  get: p => p.emissao_numero_endosso || '' }
const colTipo         = { key: 'tipo',           label: 'Tipo',             get: p => p.motivo || '', centralizada: true }
const colVigInicial   = { key: 'vig_inicial',    label: 'Vigência\nInicial', get: p => p.data_inicio || '',                    date: true, centralizada: true }
const colVigFinal     = { key: 'vig_final',      label: 'Vigência\nFinal', get: p => p.data_vencimento || '',                 date: true, centralizada: true }
const colFormaPgto    = { key: 'forma_pgto',     label: 'Forma de\nPgto',  get: p => p.forma_pagamento || '', centralizada: true }
const colDataProposta = { key: 'data_proposta',  label: 'Data da\nProposta', get: p => p.data_proposta || '',                  date: true }
const colDataEmissao  = { key: 'data_emissao',   label: 'Data da\nEmissão', get: p => p.emissao_data || '',                   date: true, centralizada: true }
const colDataEnvio    = { key: 'data_envio',     label: 'Data do\nEnvio',  get: p => p.emissao_data_envio || '',              date: true, centralizada: true }

const STATUS_DOC = {
  em_analise: { label: 'Em Análise', cls: 'badge-blue' },
  pendente:   { label: 'Pendente',   cls: 'badge-amber' },
  emitido:    { label: 'Emitido',    cls: 'badge-green' },
  recusada:   { label: 'Recusada',   cls: 'badge-gray' },
  cancelada:  { label: 'Cancelada',  cls: 'badge-red' },
  vencida:    { label: 'Vencida',    cls: 'badge-amber' },
}
const colStatus = { key: 'status_doc', label: 'Status', get: p => STATUS_DOC[p.status]?.label || '', centralizada: true }

// Filtro avancado (pedido do usuario, 26/10): fica salvo neste navegador conforme a ultima busca,
// ate o "Limpar filtros". So vale com o quadradinho "Filtro" marcado.
const CHAVE_FILTROS = 'propostas.filtros'
// Periodo num campo so, como no Corp: escolhe "pela data" (qual data) e depois inicio/fim.
const DATAS_PERIODO = [
  { campo: 'data_inicio', label: 'Vigência inicial' },
  { campo: 'data_vencimento', label: 'Vigência final' },
  { campo: 'data_proposta', label: 'Data da proposta' },
]
const FILTROS_VAZIOS = { ramos: [], seguradoras: [], tipos: [], status: [], periodoCampo: 'data_inicio', periodoDe: '', periodoAte: '' }
const temCriterio = (f) => !!(f.ramos.length || f.seguradoras.length || f.tipos.length || f.status.length || f.periodoDe || f.periodoAte)
const lerFiltrosSalvos = () => {
  try { return JSON.parse(localStorage.getItem(CHAVE_FILTROS)) || {} } catch { return {} }
}
const dia = (d) => (d ? String(d).slice(0, 10) : '')
const noPeriodo = (d, de, ate) => (!de || (!!dia(d) && dia(d) >= de)) && (!ate || (!!dia(d) && dia(d) <= ate))
const passaFiltro = (p, f) =>
  (!f.ramos.length || f.ramos.includes(p.ramo || '')) &&
  (!f.seguradoras.length || f.seguradoras.includes(p.seguradora || '')) &&
  (!f.tipos.length || f.tipos.includes(p.motivo || '')) &&
  (!f.status.length || f.status.includes(p.status)) &&
  noPeriodo(p[f.periodoCampo], f.periodoDe, f.periodoAte)

const colunasPorAba = {
  pendente: [colDias, colNome, colCpf, colRamo, colSeguradora, colNumero, colTipo, colVigInicial, colVigFinal, colFormaPgto, colDataProposta, colStatus],
  emitido:  [colNome, colCpf, colRamo, colSeguradora, colNumeroApolice, colNumeroEndosso, colVigInicial, colVigFinal, colFormaPgto],
  enviado:  [colNome, colCpf, colRamo, colSeguradora, colNumeroApolice, colNumeroEndosso, colVigInicial, colVigFinal, colFormaPgto, colDataEmissao, colDataEnvio, colStatus],
}

export default function Propostas() {
  const [documentos, setDocumentos] = useState([])
  const [documentoModal, setDocumentoModal] = useState(null)
  const [reabrindo, setReabrindo] = useState(() => !!sessionStorage.getItem('reabrirPropostaId'))
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [abaAtiva, setAbaAtiva] = useState('pendente')
  const [busca, setBusca] = useState('')
  const [mostrarSugestoesBusca, setMostrarSugestoesBusca] = useState(false)
  const [usarFiltro, setUsarFiltro] = useState(() => !!lerFiltrosSalvos().usarFiltro)
  // So as chaves atuais (filtro salvo na versao anterior, com 3 pares de datas, e' ignorado nesses campos)
  const [filtros, setFiltros] = useState(() => {
    const salvos = lerFiltrosSalvos().filtros || {}
    return Object.fromEntries(Object.entries(FILTROS_VAZIOS).map(([k, v]) => [k, salvos[k] ?? v]))
  })
  const setFiltro = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }))
  useEffect(() => {
    try { localStorage.setItem(CHAVE_FILTROS, JSON.stringify({ usarFiltro, filtros })) } catch { /* sem localStorage: so nao persiste */ }
  }, [usarFiltro, filtros])
  const [acoesAbertoId, setAcoesAbertoId] = useState(null)
  // Posicao do menu de Acoes em coordenadas de viewport (position:fixed) — nao pode ser
  // position:absolute relativo ao table-wrap porque esse (overflow-x:auto, que forca
  // overflow-y:auto tambem) corta o menu sempre que a tabela tem poucas linhas (ex.: busca
  // filtrando pra 1 resultado so): nem abrindo pra baixo nem pra cima sobra espaco dentro do
  // wrap pro menu inteiro. Fixed escapa desse clipping e usa a janela toda como referencia.
  const [acoesMenuPos, setAcoesMenuPos] = useState(null)
  const [escolhaEmissao, setEscolhaEmissao] = useState(null)
  const [importandoApolice, setImportandoApolice] = useState(false)
  const [resultadoImportacao, setResultadoImportacao] = useState(null)
  const [importacaoLote, setImportacaoLote] = useState(null)
  const [lendoLote, setLendoLote] = useState(false)
  const [salvandoLote, setSalvandoLote] = useState(false)
  const [importacaoLoteApolice, setImportacaoLoteApolice] = useState(null)
  const [lendoLoteApolice, setLendoLoteApolice] = useState(false)
  const [salvandoLoteApolice, setSalvandoLoteApolice] = useState(false)
  const acoesMenuRef = useRef(null)
  const tableWrapRef = useRef(null)
  const pdfApoliceInputRef = useRef(null)
  const pdfPropostasInputRef = useRef(null)
  const pdfApolicesInputRef = useRef(null)
  useEffect(() => {
    if (acoesAbertoId == null) return
    const handler = (e) => { if (acoesMenuRef.current && !acoesMenuRef.current.contains(e.target)) setAcoesAbertoId(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [acoesAbertoId])

  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const criarProposta = (clienteId, extras = {}) => {
    setDocumentoModal({
      modo: 'novo',
      initialFormOverrides: {
        tipo_documento: 'Proposta',
        fase_documento: 'Proposta',
        status: 'em_analise',
        motivo: 'Novo',
        forma_pagamento: 'Boleto',
        ...(clienteId ? { cliente_id: clienteId } : {}),
        ...extras
      }
    })
  }

  const chaveProposta = dados => {
    const numero = normalizarChave(dados?.numero_proposta)
    return numero ? `${normalizarChave(dados?.seguradora)}:${numero}` : ''
  }

  const atualizarItemLote = (id, atualizacao) => {
    setImportacaoLote(atual => atual ? {
      ...atual,
      itens: atual.itens.map(item => item.id === id ? { ...item, ...atualizacao } : item)
    } : atual)
  }

  const selecionarPropostas = async (event) => {
    const selecionados = Array.from(event.target.files || [])
    event.target.value = ''
    if (!selecionados.length) return

    const arquivos = selecionados.slice(0, 30)
    const itens = arquivos.map((file, indice) => ({
      id: `${Date.now()}-${indice}`,
      file,
      nome: file.name,
      status: 'aguardando',
      selecionado: false,
      dados: null,
      erro: ''
    }))
    setImportacaoLote({ itens, excedentes: Math.max(0, selecionados.length - arquivos.length) })
    setLendoLote(true)

    const chavesExistentes = new Set(documentos.map(chaveProposta).filter(Boolean))
    const chavesDoLote = new Set()
    for (const item of itens) {
      if ((!item.file.type && !item.nome.toLowerCase().endsWith('.pdf')) || (item.file.type && item.file.type !== 'application/pdf' && !item.nome.toLowerCase().endsWith('.pdf'))) {
        atualizarItemLote(item.id, { status: 'erro', erro: 'O arquivo não é um PDF.' })
        continue
      }
      if (item.file.size > 20 * 1024 * 1024) {
        atualizarItemLote(item.id, { status: 'erro', erro: 'O arquivo ultrapassa 20 MB.' })
        continue
      }
      atualizarItemLote(item.id, { status: 'lendo' })
      try {
        const formData = new FormData()
        formData.append('pdf', item.file)
        const { data } = await api.post('/apolices/importar-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const chave = chaveProposta(data)
        const duplicada = !!chave && (chavesExistentes.has(chave) || chavesDoLote.has(chave))
        if (chave) chavesDoLote.add(chave)
        atualizarItemLote(item.id, {
          dados: data,
          // Endosso sugerido (mesmo cliente+placa+seguradora+vigência final de uma apólice já
          // cadastrada, ver detectarEndossoSugerido no backend) prevalece sobre a checagem de
          // duplicata por nº de proposta — o usuário decide na tela, nunca decide sozinho.
          // Endosso de apolice que nao esta no sistema: nao cadastra como proposta nova.
          status: data.endosso_sugerido ? 'endosso_sugerido' : data.endosso_sem_apolice ? 'endosso_sem_apolice' : (duplicada ? 'duplicada' : 'pronta'),
          endossoInfo: data.endosso_sugerido || null,
          selecionado: !duplicada && !data.endosso_sugerido && !data.endosso_sem_apolice,
          erro: data.endosso_sugerido ? '' : data.endosso_sem_apolice ? textoEndossoSemApolice(data) : (duplicada ? 'Esta proposta já está cadastrada ou repetida neste lote.' : '')
        })
      } catch (err) {
        atualizarItemLote(item.id, {
          status: 'erro',
          erro: err.response?.data?.erro || 'Não foi possível ler este PDF.'
        })
      }
    }
    setLendoLote(false)
  }

  // Usuário confirma no item do lote que a sugestão de Endosso (ver selecionarPropostas) está
  // errada: some com o vinculo e reaplica a checagem normal de duplicata por nº de proposta, igual
  // ao que rodaria se o backend nao tivesse sugerido endosso pra esse PDF.
  const recusarEndossoLote = (item) => {
    const chavesExistentes = new Set(documentos.map(chaveProposta).filter(Boolean))
    const chavesDoLote = new Set(
      (importacaoLote?.itens || [])
        .filter(i => i.id !== item.id && i.dados)
        .map(i => chaveProposta(i.dados))
        .filter(Boolean)
    )
    const chave = chaveProposta(item.dados)
    const duplicada = !!chave && (chavesExistentes.has(chave) || chavesDoLote.has(chave))
    atualizarItemLote(item.id, {
      status: duplicada ? 'duplicada' : 'pronta',
      endossoInfo: null,
      selecionado: !duplicada,
      erro: duplicada ? 'Esta proposta já está cadastrada ou repetida neste lote.' : ''
    })
  }

  // Usuário decide, na pergunta "É um endosso?", nem cadastrar como Endosso nem como proposta nova
  // — remove o item do lote (não cadastra nada com esse PDF).
  const excluirItemLote = (id) => {
    setImportacaoLote(atual => atual ? { ...atual, itens: atual.itens.filter(i => i.id !== id) } : atual)
  }

  // Usuário escolheu Tipo de Endosso e clicou em "Cadastrar Endosso" no proprio item do lote —
  // cadastra direto (ver cadastrarItemLoteComoEndosso), sem abrir a tela de Proposta.
  const confirmarEndossoLote = async (item) => {
    if (!item.endossoTipo) return
    atualizarItemLote(item.id, { status: 'cadastrando', erro: '' })
    try {
      const endossoId = await cadastrarItemLoteComoEndosso(item, item.endossoTipo, item.endossoObs || '')
      atualizarItemLote(item.id, { status: 'cadastrada', selecionado: false, propostaId: endossoId })
      await carregar()
    } catch (err) {
      atualizarItemLote(item.id, { status: 'endosso_sugerido', endossoExpandido: true, erro: err.response?.data?.erro || err.message })
    }
  }

  const arquivoComoDataUrl = file => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

  const cadastrarItemLote = async item => {
    const dados = item.dados || {}
    let clienteId = dados.cliente_id
    // Cliente ja cadastrado: antes de cadastrar a proposta, mostra o que a proposta traz de
    // diferente do cadastro (endereco/telefone/e-mail/estado civil/profissao), cliente por cliente.
    if (clienteId) await conferirDadosCliente(clienteId, dados)
    if (!clienteId) {
      if (!dados.cliente_nome?.trim() || !dados.cliente_cpf?.trim()) {
        throw new Error('Nome ou CPF/CNPJ do segurado não foi localizado no PDF.')
      }
      const cliente = await api.post('/clientes', {
        nome: dados.cliente_nome,
        cpf_cnpj: dados.cliente_cpf,
        sexo: dados.cliente_sexo || '',
        data_nascimento: dados.cliente_data_nascimento || '',
        rg: dados.cliente_rg || '',
        estado_civil: dados.cliente_estado_civil || '',
        profissao: dados.cliente_profissao || '',
        cep: dados.cliente_cep || '',
        endereco: dados.cliente_endereco || '',
        numero: dados.cliente_numero || '',
        complemento: dados.cliente_complemento || '',
        bairro: dados.cliente_bairro || '',
        cidade: dados.cliente_cidade || '',
        estado: dados.cliente_estado || '',
        telefones: dados.cliente_telefone ? [{ numero: dados.cliente_telefone, nome: '' }] : [],
        emails: dados.cliente_email ? [dados.cliente_email] : []
      })
      clienteId = cliente.data.id
    }

    const premioLiquido = parseValor(dados.premio_liquido)
    // HDI DUO: liquido do residencial combinado (PL DUO) tambem gera comissao
    const plDuo = parseValor(dados.pl_duo)
    const percentualComissao = parseValor(dados.comissao_pct)
    // !== 0 (nao > 0): premio liquido negativo (endosso importado como proposta, ex.: usuario
    // recusou a sugestao de Endosso) tem que refletir numa comissao tambem negativa.
    const valorComissao = premioLiquido + plDuo !== 0 && percentualComissao > 0 ? (premioLiquido + plDuo) * percentualComissao / 100 : 0
    const restituicao = parseValor(dados.premio_total) < 0
    const parcelas = Array.isArray(dados.parcelas) ? dados.parcelas.map((parcela, indice) => ({
      numero: parcela.numero || indice + 1,
      vencimento: parcela.vencimento || '',
      valor: parcela.valor ?? '',
      forma_pagamento: restituicao ? 'Restituição' : (parcela.forma_pagamento || dados.forma_pagamento || 'Boleto'),
      status: parcela.status || 'Pendente'
    })) : []
    const parcelasComissao = parcelas.length ? [{
      numero: 1,
      data: somarDias(parcelas[0].vencimento, 15),
      valor: valorComissao !== 0 ? formatMoney(valorComissao) : '',
      status: 'Pendente'
    }] : []

    const proposta = await api.post('/apolices', {
      ...dados,
      cliente_id: clienteId,
      tipo_documento: 'Proposta',
      fase_documento: 'Proposta',
      status: 'em_analise',
      motivo: dados.motivo || 'Novo',
      vigencia_inicio: dados.vigencia_inicio || '',
      vigencia_fim: dados.vigencia_fim || '',
      forma_pagamento: restituicao ? 'Restituição' : (dados.forma_pagamento || 'Boleto'),
      quantidade_parcelas: dados.quantidade_parcelas || parcelas.length,
      parcelas,
      premio_liquido: premioLiquido,
      pl_duo: plDuo,
      premio_total: parseValor(dados.premio_total),
      iof_valor: parseValor(dados.iof_valor),
      adicional: parseValor(dados.adicional),
      comissao_pct: dados.comissao_pct || '',
      comissao: valorComissao,
      forma_recebimento_comissao: valorComissao < 0 ? 'Restituição' : 'À vista',
      parcelas_comissao: parcelasComissao
    })
    const propostaId = proposta.data.id

    if (['Automóvel', 'Carta Verde', 'Residencial', 'Empresarial'].includes(dados.ramo)) {
      // Frota (ex.: Porto Auto Frota): um item por veiculo, vindo em `dados.itens`
      const { itens: itensPdf, ...dadosSemItens } = dados
      const fontes = Array.isArray(itensPdf) && itensPdf.length > 1 ? itensPdf.map(it => ({ ...dadosSemItens, ...it })) : [dadosSemItens]
      for (const [indice, fonte] of fontes.entries()) {
        await api.post('/itens', {
          ...fonte,
          apolice_id: propostaId,
          ramo: dados.ramo,
          descricao_item: fonte.item_modelo || fonte.item_placa || fonte.res_endereco || `Item ${indice + 1}`,
          coberturas_dinamicas: fonte.coberturas_dinamicas || {}
        })
      }
    }

    const conteudo = await arquivoComoDataUrl(item.file)
    await api.post(`/apolices/${propostaId}/anexos`, {
      tipo: 'Proposta', nome: item.file.name, tamanho: item.file.size, dados: conteudo
    })
    return propostaId
  }

  // Cadastra o item do lote como Endosso da apólice já identificada (item.endossoInfo.apolice_id),
  // sem abrir a tela de Proposta: segurado/seguradora/ramo/nº da apólice vêm TRAVADOS do documento
  // original (mesmo padrão do fluxo manual "Endossar" em Apolices.jsx), só o financeiro/coberturas
  // vem do PDF do endosso; Tipo de Endosso e Observação são escolhidos aqui mesmo, no próprio lote.
  const cadastrarItemLoteComoEndosso = async (item, tipoEndosso, observacao) => {
    const dados = item.dados || {}
    const { data: original } = await api.get(`/apolices/${item.endossoInfo.apolice_id}`)
    const { data: itensOriginais } = await api.get(`/itens/apolice/${original.id}`).catch(() => ({ data: [] }))

    const premioLiquido = parseValor(dados.premio_liquido)
    const plDuo = parseValor(dados.pl_duo)
    const percentualComissao = parseValor(dados.comissao_pct) || parseValor(original.comissao_pct)
    const valorComissao = premioLiquido + plDuo !== 0 && percentualComissao > 0 ? (premioLiquido + plDuo) * percentualComissao / 100 : 0
    const premioTotal = parseValor(dados.premio_total)
    // Prêmio negativo (endosso com restituição ao segurado): parcelas e recebimento da comissão
    // viram "Restituição" automaticamente — mesmo padrão do fluxo manual (gerarParcelas/
    // calcularValores em Apolices.jsx).
    const restituicao = premioTotal < 0
    const parcelas = Array.isArray(dados.parcelas) ? dados.parcelas.map((parcela, indice) => ({
      numero: parcela.numero || indice + 1,
      vencimento: parcela.vencimento || '',
      valor: parcela.valor ?? '',
      forma_pagamento: restituicao ? 'Restituição' : (parcela.forma_pagamento || dados.forma_pagamento || 'Boleto'),
      status: parcela.status || 'Pendente'
    })) : []
    const parcelasComissao = parcelas.length ? [{
      numero: 1,
      data: somarDias(parcelas[0].vencimento, 15),
      valor: valorComissao !== 0 ? formatMoney(valorComissao) : '',
      status: 'Pendente'
    }] : []

    const endosso = await api.post('/apolices', {
      cliente_id: original.cliente_id,
      seguradora: original.seguradora,
      ramo: original.ramo,
      numero_apolice: original.numero_apolice,
      numero_apolice_renovada: original.numero_apolice_renovada || '',
      emissao_numero_apolice: original.emissao_numero_apolice || original.numero_apolice || '',
      eh_endosso: true,
      tipo_documento: 'Proposta',
      fase_documento: 'Proposta',
      status: 'em_analise',
      motivo: tipoEndosso,
      endosso_observacao: observacao,
      documento_origem_id: original.id,
      documento_raiz_id: original.documento_raiz_id || original.id,
      numero_proposta: dados.numero_proposta || '',
      data_proposta: dados.data_proposta || '',
      vigencia_inicio: dados.vigencia_inicio || '',
      vigencia_fim: dados.vigencia_fim || original.data_vencimento?.split('T')[0] || '',
      forma_pagamento: restituicao ? 'Restituição' : (dados.forma_pagamento || 'Boleto'),
      quantidade_parcelas: dados.quantidade_parcelas || parcelas.length,
      parcelas,
      premio_liquido: premioLiquido,
      pl_duo: plDuo,
      premio_total: premioTotal,
      iof_valor: parseValor(dados.iof_valor),
      adicional: parseValor(dados.adicional),
      iof_pct: original.iof_pct || '7,38',
      comissao_pct: dados.comissao_pct || original.comissao_pct || '',
      comissao: valorComissao,
      forma_recebimento_comissao: valorComissao < 0 ? 'Restituição' : 'À vista',
      parcelas_comissao: parcelasComissao
    })
    const endossoId = endosso.data.id

    // Copia os itens da apólice original pro endosso, atualizando as coberturas que o PDF trouxe
    // (mesmo ponto de partida do fluxo manual, que copia `itensParaCopiar` a partir da ponta da
    // cadeia antes de o usuário revisar e salvar).
    const itensBase = itensOriginais.length ? itensOriginais : [{ ramo: original.ramo }]
    // Endosso de substituicao de veiculo: troca os dados do carro pelos do PDF (placa em branco
    // se o carro novo ainda nao tem). Marcado pelo leitor ou detectado pelo chassi diferente.
    const chassiOriginal = String(itensBase[0]?.item_chassi || '').toUpperCase()
    const chassiTrocado = !!dados.item_chassi && !!chassiOriginal && String(dados.item_chassi).toUpperCase() !== chassiOriginal
    const veiculoNovo = (dados.endosso_substituicao_veiculo || chassiTrocado)
      ? Object.fromEntries(['item_fipe', 'item_modelo', 'item_ano_fab', 'item_ano_mod', 'item_zero_km', 'item_placa', 'item_chassi', 'item_cor', 'item_passageiros', 'item_combustivel']
        .map(c => [c, dados[c] || (c === 'item_zero_km' ? 'Não' : c === 'item_combustivel' ? 'Flex' : '')])
        .concat([['descricao_item', dados.item_modelo || dados.item_placa || '']]))
      : {}
    // Condutor/perfil podem mudar junto no endosso — o que o PDF trouxer vale por cima do item copiado.
    const perfilNovo = Object.fromEntries(['cond_nome', 'cond_cpf', 'cond_nascimento', 'cond_estado_civil', 'item_uso', 'item_condutores_jovens', 'item_bonus']
      .filter(c => dados[c]).map(c => [c, dados[c]]))
    if (dados.endosso_frota && Array.isArray(dados.itens)) {
      // Endosso de frota: repete os itens da apólice e aplica exclusão/inclusão/alteração do PDF.
      for (const { original, fonte } of montarItensEndossoFrota(itensOriginais, dados.itens)) {
        const { id, apolice_id, created_at, ...base } = original || { ramo: dados.ramo }
        await api.post('/itens', fonte ? {
          ...base,
          ...fonte,
          ramo: base.ramo || dados.ramo,
          descricao_item: fonte.item_modelo || fonte.item_placa || base.descricao_item || '',
          apolice_id: endossoId,
          coberturas_dinamicas: { ...(base.coberturas_dinamicas || {}), ...(fonte.coberturas_dinamicas || {}) }
        } : { ...base, apolice_id: endossoId })
      }
    } else for (const itemOriginal of itensBase) {
      await api.post('/itens', {
        ...itemOriginal,
        ...veiculoNovo,
        ...perfilNovo,
        apolice_id: endossoId,
        coberturas_dinamicas: { ...(itemOriginal.coberturas_dinamicas || {}), ...(dados.coberturas_dinamicas || {}) }
      })
    }

    const conteudo = await arquivoComoDataUrl(item.file)
    await api.post(`/apolices/${endossoId}/anexos`, {
      tipo: 'Proposta', nome: item.file.name, tamanho: item.file.size, dados: conteudo
    })
    return endossoId
  }

  const cadastrarLote = async () => {
    const selecionados = importacaoLote?.itens.filter(item => item.selecionado && item.status === 'pronta') || []
    if (!selecionados.length) return
    setSalvandoLote(true)
    for (const item of selecionados) {
      atualizarItemLote(item.id, { status: 'cadastrando', erro: '' })
      try {
        const propostaId = await cadastrarItemLote(item)
        atualizarItemLote(item.id, { status: 'cadastrada', selecionado: false, propostaId })
      } catch (err) {
        atualizarItemLote(item.id, { status: 'erro_cadastro', selecionado: false, erro: err.response?.data?.erro || err.message })
      }
    }
    await carregar()
    setSalvandoLote(false)
  }

  const atualizarItemLoteApolice = (id, atualizacao) => {
    setImportacaoLoteApolice(atual => atual ? {
      ...atual,
      itens: atual.itens.map(item => item.id === id ? { ...item, ...atualizacao } : item)
    } : atual)
  }

  // Casa o PDF de apolice com a proposta pendente correspondente: primeiro por numero da
  // proposta (extraido de qualquer seguradora — ver `parseApolicePdf.js`), senao pela placa do
  // veiculo (tambem generica, mas so existe pra Automóvel/Carta Verde — Residencial nunca tem
  // placa, entao pra esse ramo o numero da proposta e' o unico jeito de casar automatico). Sem
  // nenhum dos dois, fica sem correspondencia e o usuario escolhe manualmente na tela de
  // conferencia. So digitos/letras (sem hifen, espaco, barra) nos dois lados da comparacao —
  // uma placa cadastrada como "MLM-8C82" tem que casar com "MLM8C82" extraido do PDF.
  const soAlfanumerico = valor => String(valor || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  const casarComPropostaPendente = (extraido, pendentes, usadas) => {
    const numeroDigitos = soDigitos(extraido.numero_proposta)
    let proposta = numeroDigitos
      ? pendentes.find(p => !usadas.has(p.id) && soDigitos(p.numero_proposta) === numeroDigitos)
      : null
    if (!proposta && extraido.placa) {
      const placaExtraida = soAlfanumerico(extraido.placa)
      proposta = pendentes.find(p => !usadas.has(p.id) && p.item_placa && soAlfanumerico(p.item_placa) === placaExtraida)
    }
    // Sem nº de proposta em comum nem placa (ex.: Yelum Empresarial): mesmo CPF/CNPJ e mesmo
    // inicio de vigencia, so quando ha uma unica proposta pendente assim.
    if (!proposta && extraido.cpf_cnpj && extraido.vigencia_inicio) {
      const candidatas = pendentes.filter(p => !usadas.has(p.id)
        && soDigitos(p.cliente_cpf_cnpj) === extraido.cpf_cnpj
        && String(p.data_inicio || '').split('T')[0] === extraido.vigencia_inicio)
      if (candidatas.length === 1) proposta = candidatas[0]
    }
    return proposta || null
  }

  const selecionarApolicesLote = async (event) => {
    const selecionados = Array.from(event.target.files || [])
    event.target.value = ''
    if (!selecionados.length) return

    const arquivos = selecionados.slice(0, 30)
    const itens = arquivos.map((file, indice) => ({
      id: `${Date.now()}-${indice}`,
      file,
      nome: file.name,
      status: 'aguardando',
      selecionado: false,
      extraido: null,
      proposta: null,
      erro: ''
    }))
    setImportacaoLoteApolice({ itens, excedentes: Math.max(0, selecionados.length - arquivos.length) })
    setLendoLoteApolice(true)

    const pendentes = porAba.pendente
    const usadas = new Set()
    for (const item of itens) {
      if ((!item.file.type && !item.nome.toLowerCase().endsWith('.pdf')) || (item.file.type && item.file.type !== 'application/pdf' && !item.nome.toLowerCase().endsWith('.pdf'))) {
        atualizarItemLoteApolice(item.id, { status: 'erro', erro: 'O arquivo não é um PDF.' })
        continue
      }
      if (item.file.size > 20 * 1024 * 1024) {
        atualizarItemLoteApolice(item.id, { status: 'erro', erro: 'O arquivo ultrapassa 20 MB.' })
        continue
      }
      atualizarItemLoteApolice(item.id, { status: 'lendo' })
      try {
        const formData = new FormData()
        formData.append('pdf', item.file)
        const { data } = await api.post('/apolices/importar-apolice-preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const extraido = data.extraido || {}
        // Apolice do residencial do HDI DUO: vai pro mesmo documento da apolice do Auto (mesmo nº de
        // proposta), que pode ja estar emitido — procura em todos os documentos DUO, nao so pendentes.
        const proposta = extraido.duo_residencial
          ? documentos.find(d => (d.combinado_residencial === 'DUO' || Number(d.pl_duo)) && soDigitos(d.numero_proposta) === soDigitos(extraido.numero_proposta)) || null
          : casarComPropostaPendente(extraido, pendentes, usadas)
        if (proposta && !extraido.duo_residencial) usadas.add(proposta.id)
        atualizarItemLoteApolice(item.id, {
          extraido,
          proposta,
          status: proposta ? 'pronta' : 'sem_correspondencia',
          selecionado: !!proposta,
          erro: proposta ? '' : 'Não foi possível achar a proposta pendente correspondente. Escolha manualmente.'
        })
      } catch (err) {
        atualizarItemLoteApolice(item.id, { status: 'erro', erro: err.response?.data?.erro || 'Não foi possível ler este PDF.' })
      }
    }
    setLendoLoteApolice(false)
  }

  const escolherPropostaLoteApolice = (itemId, propostaId) => {
    const proposta = porAba.pendente.find(p => String(p.id) === String(propostaId)) || null
    atualizarItemLoteApolice(itemId, {
      proposta,
      status: proposta ? 'pronta' : 'sem_correspondencia',
      selecionado: !!proposta,
      erro: proposta ? '' : 'Escolha a proposta pendente correspondente.'
    })
  }

  const cadastrarLoteApolice = async () => {
    const selecionados = importacaoLoteApolice?.itens.filter(item => item.selecionado && item.status === 'pronta' && item.proposta) || []
    if (!selecionados.length) return
    setSalvandoLoteApolice(true)
    for (const item of selecionados) {
      atualizarItemLoteApolice(item.id, { status: 'cadastrando', erro: '' })
      try {
        const formData = new FormData()
        formData.append('pdf', item.file)
        await api.post(`/apolices/${item.proposta.id}/importar-apolice`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        atualizarItemLoteApolice(item.id, { status: 'cadastrada', selecionado: false })
      } catch (err) {
        atualizarItemLoteApolice(item.id, { status: 'erro_cadastro', selecionado: false, erro: err.response?.data?.erro || err.message })
      }
    }
    await carregar()
    setSalvandoLoteApolice(false)
  }

  // Busca o documento completo (com anexos) antes de abrir — a listagem nao traz anexos, so o detalhe traz.
  // focarEmissao: abre ja rolado ate o card Dados de Emissão, com o campo Número da Apólice em foco
  // (botao "Emitir" do menu Ações).
  const editarProposta = (proposta, focarEmissao = false, focarEnvio = false) => {
    return api.get(`/apolices/${proposta.id}`)
      .then(r => setDocumentoModal({ modo: 'editar', initialDocument: r.data, focarEmissao, focarEnvio }))
      .catch(() => setDocumentoModal({ modo: 'editar', initialDocument: proposta, focarEmissao, focarEnvio }))
  }

  const importarPdfApolice = async (event) => {
    const arquivo = event.target.files?.[0]
    event.target.value = ''
    if (!arquivo || !escolhaEmissao) return
    if (arquivo.type !== 'application/pdf' && !arquivo.name.toLowerCase().endsWith('.pdf')) {
      setResultadoImportacao({ tipo: 'erro', titulo: 'Arquivo inválido', mensagem: 'Selecione um arquivo PDF.' })
      return
    }
    if (arquivo.size > 20 * 1024 * 1024) {
      setResultadoImportacao({ tipo: 'erro', titulo: 'Arquivo muito grande', mensagem: 'O tamanho máximo permitido é 20MB.' })
      return
    }
    setImportandoApolice(true)
    try {
      const dados = new FormData()
      dados.append('pdf', arquivo)
      const resposta = await api.post(`/apolices/${escolhaEmissao.id}/importar-apolice`, dados, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const alteracoes = resposta.data?.alteracoes || []
      setEscolhaEmissao(null)
      setResultadoImportacao({
        tipo: 'sucesso',
        titulo: 'Apólice importada',
        mensagem: 'O PDF foi lido e anexado com a tag Apólice.',
        alteracoes,
        documento: escolhaEmissao
      })
      await carregar()
    } catch (err) {
      setEscolhaEmissao(null)
      setResultadoImportacao({
        tipo: 'erro',
        titulo: 'Não foi possível importar a apólice',
        mensagem: err.response?.data?.erro || err.message
      })
    } finally {
      setImportandoApolice(false)
    }
  }

  const carregar = () => {
    return api.get('/apolices').then((apolicesRes) => {
      setDocumentos(apolicesRes.data)
    })
  }

  useEffect(() => {
    carregar().then(() => {
      // Reabrir a proposta que estava sendo editada (vindo da tela de Clientes)
      const reabrirId = sessionStorage.getItem('reabrirPropostaId')
      // Vindo do CRM: card movido para "Fechado" e o usuario escolheu cadastrar a proposta agora
      // (manualmente ou importando o PDF).
      const clienteIdNovaProposta = sessionStorage.getItem('abrirNovaPropostaClienteId')
      const abrirImportarPdf = sessionStorage.getItem('abrirImportarPropostaAoCarregar')
      if (reabrirId) {
        sessionStorage.removeItem('reabrirPropostaId')
        editarProposta({ id: reabrirId }).finally(() => setReabrindo(false))
      } else if (clienteIdNovaProposta) {
        sessionStorage.removeItem('abrirNovaPropostaClienteId')
        let origem = null
        try { origem = JSON.parse(sessionStorage.getItem('abrirNovaPropostaOrigem') || 'null') } catch { origem = null }
        sessionStorage.removeItem('abrirNovaPropostaOrigem')
        if (origem?.id) {
          // Card de renovacao do CRM: mesmos campos que o "Renovar" da apolice preenche.
          api.get(`/apolices/${origem.id}`).then(({ data: a }) => {
            criarProposta(clienteIdNovaProposta, {
              motivo: origem.motivo || 'Renovação Interna',
              seguradora: a.seguradora || '',
              ramo: a.ramo || '',
              numero_apolice_renovada: a.emissao_numero_apolice || a.numero_apolice || '',
              comissao_pct: a.comissao_pct || '',
              documento_origem_id: a.id,
              documento_raiz_id: a.documento_raiz_id || a.id,
            })
          }).catch(() => criarProposta(clienteIdNovaProposta))
        } else {
          criarProposta(clienteIdNovaProposta)
        }
        setReabrindo(false)
      } else if (abrirImportarPdf) {
        sessionStorage.removeItem('abrirImportarPropostaAoCarregar')
        setReabrindo(false)
        pdfPropostasInputRef.current?.click()
      } else {
        setReabrindo(false)
      }
    })
  }, [])

  // Uma apolice emitida aparece aqui (Emitidas/Enviadas) e tambem na aba Apolice, ao mesmo tempo —
  // nao depende de tipo_documento/fase_documento, so do status e da data de envio.
  // Endosso passa pelo mesmo fluxo: enquanto pendente e uma "proposta de endosso" (tipo_documento
  // ainda 'Proposta'); ao ser emitido vira tipo_documento 'Endosso', mas continua aparecendo aqui
  // (Emitidas/Enviadas) alem de no historico da apolice.
  // Cancelada nao some: um documento so vira 'cancelada' depois de ja ter sido emitido (endosso de
  // cancelamento exige Nº do Endosso preenchido), entao continua na aba Emitidas/Enviadas onde
  // sempre esteve, so que agora com o status "Cancelada" na listagem.
  // 'vencida' tambem conta como "ja foi emitido": um documento cadastrado com a vigencia ja
  // encerrada (ex.: retroativo/teste) vira 'vencida' direto, sem nunca passar por 'emitido'.
  const foiEmitido = a => a.status === 'emitido' || a.status === 'cancelada' || a.status === 'vencida'
  // "Pendente" aqui agrupa tudo que ainda nao foi emitido: em analise, pendente de ajuste e
  // recusada (recusada e terminal, mas continua visivel aqui, so com o badge "Recusada").
  // Filtro avancado vale pras 3 abas (e os contadores das abas mostram o que sobrou filtrado).
  const docsFiltrados = documentos.filter(p => passaFiltro(p, filtros))
  const porAba = {
    pendente: docsFiltrados.filter(a => ['em_analise', 'pendente', 'recusada'].includes(a.status)),
    emitido: docsFiltrados.filter(a => foiEmitido(a) && !a.emissao_data_envio),
    enviado: docsFiltrados.filter(a => foiEmitido(a) && a.emissao_data_envio),
  }
  const opcoesDe = (campo) => [...new Set(documentos.map(p => p[campo]).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR')).map(v => ({ valor: v, label: v }))
  const opcoesStatus = Object.entries(STATUS_DOC).map(([valor, s]) => ({ valor, label: s.label }))
  const filtrosAtivos = JSON.stringify(filtros) !== JSON.stringify(FILTROS_VAZIOS)

  const filtrarBusca = (lista) => {
    const q = busca.toLowerCase().trim()
    if (!q) return lista
    const qRaw = q.replace(/\D/g, '')
    return lista.filter(p =>
      p.cliente_nome?.toLowerCase().includes(q) ||
      (p.cliente_cpf_cnpj && (p.cliente_cpf_cnpj.includes(q) || (qRaw && p.cliente_cpf_cnpj.replace(/\D/g, '').includes(qRaw)))) ||
      p.numero_apolice?.toLowerCase().includes(q) ||
      p.emissao_numero_apolice?.toLowerCase().includes(q) ||
      p.numero_proposta?.toLowerCase().includes(q) ||
      p.emissao_numero_endosso?.toLowerCase().includes(q)
    )
  }

  const colunas = colunasPorAba[abaAtiva]
  const listaAtiva = filtrarBusca(porAba[abaAtiva])
  const listaOrdenada = sortRows(listaAtiva, colunas, sortCol, sortDir)
  const paginacao = usePaginacao(listaOrdenada)
  // A qual aba um documento pertence (mesmo criterio de porAba, so que pra 1 documento so em vez
  // de particionar a lista inteira) — usado pra pular pra aba certa ao clicar numa sugestao.
  const abaDoDocumento = (p) => {
    if (['em_analise', 'pendente', 'recusada'].includes(p.status)) return 'pendente'
    return foiEmitido(p) && p.emissao_data_envio ? 'enviado' : 'emitido'
  }
  // Sugestoes do dropdown da busca: procura em TODAS as abas (Pendentes/Emitidas/Enviadas), nao so
  // na aba aberta no momento — senao um cliente com proposta so nas Emitidas nunca aparece pra
  // sugestao enquanto a aba Pendentes estiver selecionada. Mostra so o dado que efetivamente bateu
  // (nome, CPF/CNPJ ou numero), sem repetir o mesmo cliente varias vezes por causa de endossos.
  const sugestoesBusca = (() => {
    const q = busca.toLowerCase().trim()
    if (!q) return []
    const qRaw = q.replace(/\D/g, '')
    const vistos = new Map()
    for (const p of documentos) {
      let valor = null
      if (p.cliente_nome?.toLowerCase().includes(q)) valor = p.cliente_nome
      else if (p.cliente_cpf_cnpj && (p.cliente_cpf_cnpj.includes(q) || (qRaw && p.cliente_cpf_cnpj.replace(/\D/g, '').includes(qRaw)))) valor = p.cliente_cpf_cnpj
      else if (p.numero_apolice?.toLowerCase().includes(q)) valor = p.numero_apolice
      else if (p.emissao_numero_apolice?.toLowerCase().includes(q)) valor = p.emissao_numero_apolice
      else if (p.numero_proposta?.toLowerCase().includes(q)) valor = p.numero_proposta
      else if (p.emissao_numero_endosso?.toLowerCase().includes(q)) valor = p.emissao_numero_endosso
      if (!valor) continue
      if (!vistos.has(valor)) { if (vistos.size >= 8) continue; vistos.set(valor, new Set()) }
      vistos.get(valor).add(abaDoDocumento(p))
    }
    return [...vistos.entries()].map(([valor, abas]) => ({ chave: valor, valor, abas }))
  })()
  const navBusca = useNavegacaoLista(sugestoesBusca.length, (i) => {
    const s = sugestoesBusca[i]
    setMostrarSugestoesBusca(false)
    setBusca(s.valor)
    if (!s.abas.has(abaAtiva)) setAbaAtiva(['pendente', 'emitido', 'enviado'].find(a => s.abas.has(a)))
  })

  return (
    <>
      {reabrindo && !documentoModal && (
        <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>Carregando...</div>
      )}
      {!documentoModal && !reabrindo && (<>
      <div style={{ padding: '32px 24px 0' }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Acompanhamento de Propostas</h2>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>Acompanhe suas propostas do envio até a emissão.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ position: 'relative', maxWidth: 420, width: '100%' }}>
            <input className="form-input" placeholder="Buscar por nome, CPF/CNPJ ou nº da apólice/proposta..." value={busca}
              onChange={e => { setBusca(e.target.value); setMostrarSugestoesBusca(true); navBusca.reset() }}
              onFocus={() => setMostrarSugestoesBusca(true)}
              onBlur={() => setTimeout(() => setMostrarSugestoesBusca(false), 150)}
              onKeyDown={navBusca.onKeyDown}
              style={{ width: '100%', border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
            {mostrarSugestoesBusca && busca.trim() && sugestoesBusca.length > 0 && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 320, overflowY: 'auto' }}>
                {sugestoesBusca.map((s, i) => (
                  <button key={s.chave} type="button" onMouseDown={() => {
                    setMostrarSugestoesBusca(false)
                    setBusca(s.valor)
                    if (!s.abas.has(abaAtiva)) setAbaAtiva(['pendente', 'emitido', 'enviado'].find(a => s.abas.has(a)))
                  }}
                    onMouseEnter={() => navBusca.setIndice(i)}
                    style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navBusca.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5', fontSize: 13, color: '#1A1A2E' }}>
                    {s.valor}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setUsarFiltro(v => !v)}>
            {usarFiltro ? 'Fechar filtros' : 'Abrir filtros'}{temCriterio(filtros) && <span style={{ color: 'var(--primary)', marginLeft: 6 }}>●</span>}
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button className="btn btn-secondary" onClick={() => pdfPropostasInputRef.current?.click()}>Importar Propostas</button>
            <button className="btn btn-secondary" onClick={() => pdfApolicesInputRef.current?.click()}>Importar Apólices</button>
            <button className="btn btn-primary" onClick={() => criarProposta()}>+ Criar Proposta</button>
          </div>
        </div>
        {usarFiltro && (
          <div className="card" style={{ marginBottom: 20, padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
              <SelecaoMultipla label="Ramo" opcoes={opcoesDe('ramo')} valores={filtros.ramos} onChange={v => setFiltro('ramos', v)} />
              <SelecaoMultipla label="Seguradora" opcoes={opcoesDe('seguradora')} valores={filtros.seguradoras} onChange={v => setFiltro('seguradoras', v)} />
              <SelecaoMultipla label="Tipo" opcoes={opcoesDe('motivo')} valores={filtros.tipos} onChange={v => setFiltro('tipos', v)} />
              <SelecaoMultipla label="Status" opcoes={opcoesStatus} valores={filtros.status} onChange={v => setFiltro('status', v)} />
              <div style={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Período — pela data</label>
                  <select className="form-select" value={filtros.periodoCampo} onChange={e => setFiltro('periodoCampo', e.target.value)}>
                    {DATAS_PERIODO.map(d => <option key={d.campo} value={d.campo}>{d.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Início</label>
                  <input className="form-input" type="date" value={filtros.periodoDe} onChange={e => setFiltro('periodoDe', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Fim</label>
                  <input className="form-input" type="date" value={filtros.periodoAte} onChange={e => setFiltro('periodoAte', e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" disabled={!filtrosAtivos} onClick={() => setFiltros(FILTROS_VAZIOS)}>Limpar filtros</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="page-content" style={{ paddingTop: 0 }}>
        {/* Abas como pastas: todas encostadas no card; a ativa fica na frente (colorida, emendada no card)
            e as outras ficam atras, brancas */}
        <div style={{ display: 'flex', gap: 14, padding: '0 14px', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
          {ABAS.map(a => {
            const ativo = abaAtiva === a.key
            return (
              <button key={a.key} type="button" onClick={() => { setAbaAtiva(a.key); setSortCol(null); setSortDir('asc') }} style={{
                flex: 1, position: 'relative', cursor: 'pointer',
                padding: ativo ? '14px 20px 16px' : '11px 20px',
                borderRadius: '12px 12px 0 0',
                border: `2px solid ${ativo ? a.cor : '#E3E6EA'}`,
                borderBottom: 'none',
                marginBottom: ativo ? -2 : 0,
                background: ativo ? a.cor : '#fff',
                color: ativo ? '#fff' : '#555',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontWeight: 700, fontSize: 14, fontFamily: 'inherit', transition: 'background 0.15s'
              }}>
                {a.label}
                <span style={{
                  background: ativo ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
                  color: ativo ? '#fff' : '#555',
                  padding: '2px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700
                }}>{porAba[a.key].length}</span>
              </button>
            )
          })}
        </div>

        <div className="card" style={{ border: `2px solid ${ABAS.find(a => a.key === abaAtiva).cor}` }}>
          <div className="table-wrap" ref={tableWrapRef}>
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunas.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(p => {
                  // Cancelada e Recusada sao terminais: ficam visiveis, so com a fonte apagada.
                  const encerrado = p.status === 'cancelada' || p.status === 'recusada'
                  // td tem `color: #333` fixo no CSS global, entao herdar cor cinza so pelo <tr> nao
                  // funciona — precisa aplicar a cor em cada <td> individualmente.
                  const corEncerrado = encerrado ? { color: '#bbb' } : null
                  return (
                  <tr key={p.id}>
                    {colunas.map(c => {
                      const valor = c.get(p)
                      if (c.key === 'dias') {
                        return (
                          <td key={c.key} style={{ textAlign: 'center', fontWeight: 600, color: encerrado ? '#bbb' : (valor > 30 ? '#b91c1c' : '#374151') }}>
                            {valor !== null ? valor : '—'}
                          </td>
                        )
                      }
                      if (c.key === 'status_doc') {
                        const sd = STATUS_DOC[p.status]
                        return (
                          <td key={c.key} style={{ textAlign: 'center' }}>
                            {sd ? <span className={`badge ${sd.cls}`}>{sd.label}</span> : '—'}
                          </td>
                        )
                      }
                      // Nas abas Emitidas/Enviadas a coluna Tipo (motivo) nao existe separada — vem
                      // como subtitulo embaixo do nome, igual na Consulta de Apólice.
                      if (c.key === 'nome' && !colunas.some(x => x.key === 'tipo')) {
                        return (
                          <td key={c.key} style={{ fontWeight: 600, ...corEncerrado }}>
                            <div>{valor || '—'}</div>
                            {p.motivo && <div style={{ fontSize: 11, fontWeight: 400, color: encerrado ? '#bbb' : '#888' }}>{p.motivo}</div>}
                          </td>
                        )
                      }
                      const numeroCancelado = encerrado && (c.key === 'numero_apolice' || c.key === 'numero_endosso')
                      return (
                        <td key={c.key} style={{
                          ...(c.key === 'nome' ? { fontWeight: 600 } : null),
                          ...(c.centralizada ? { textAlign: 'center' } : null),
                          ...corEncerrado,
                          ...(numeroCancelado ? { textDecoration: 'line-through' } : null)
                        }}>
                          {c.date ? fmtDate(valor) : (valor || '—')}
                          {c.key === 'ramo' && p.combinado_residencial && <div><EtiquetaCombinado tipo={p.combinado_residencial} /></div>}
                        </td>
                      )
                    })}
                    <td>
                      {abaAtiva === 'enviado' ? (
                        <button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} onClick={() => {
                          setReabrindo(true)
                          editarProposta(p).finally(() => setReabrindo(false))
                        }}><IconVisualizar size={14} /></button>
                      ) : (
                        <div ref={acoesAbertoId === p.id ? acoesMenuRef : null} style={{ position: 'relative' }}>
                          <button className="btn btn-secondary btn-sm btn-icon" style={{ color: 'var(--primary)' }} onClick={(e) => {
                            if (acoesAbertoId === p.id) { setAcoesAbertoId(null); return }
                            // Abre pra cima so quando ha mais espaco na janela ali do que embaixo —
                            // usa a janela inteira (nao o table-wrap) porque o menu e' position:fixed.
                            const rectBotao = e.currentTarget.getBoundingClientRect()
                            const espacoAbaixo = window.innerHeight - rectBotao.bottom
                            const espacoAcima = rectBotao.top
                            const abrirCima = espacoAbaixo < 110 && espacoAcima > espacoAbaixo
                            setAcoesMenuPos({
                              right: window.innerWidth - rectBotao.right,
                              ...(abrirCima ? { bottom: window.innerHeight - rectBotao.top + 4 } : { top: rectBotao.bottom + 4 }),
                            })
                            setAcoesAbertoId(p.id)
                          }} title="Ações"><IconAcoes size={14} /></button>
                          {acoesAbertoId === p.id && acoesMenuPos && (
                            <div style={{
                              position: 'fixed', zIndex: 9999, ...acoesMenuPos,
                              background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 120, overflow: 'hidden'
                            }}>
                              <button type="button" onClick={() => {
                                setAcoesAbertoId(null); setReabrindo(true)
                                editarProposta(p).finally(() => setReabrindo(false))
                              }} style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                background: 'none', border: 'none', cursor: 'pointer', fontSize: 13
                              }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Ver</button>
                              {/* Recusada e terminal: nao pode mais ser emitida. */}
                              {abaAtiva === 'pendente' && p.status !== 'recusada' && (
                                <button type="button" onClick={() => {
                                  setAcoesAbertoId(null)
                                  setEscolhaEmissao(p)
                                }} style={{
                                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13
                                }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Emitir</button>
                              )}
                              {abaAtiva === 'emitido' && (
                                <button type="button" onClick={() => {
                                  setAcoesAbertoId(null); setReabrindo(true)
                                  editarProposta(p, false, true).finally(() => setReabrindo(false))
                                }} style={{
                                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13
                                }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Enviar</button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  )
                })}
                {listaOrdenada.length === 0 && (
                  <tr>
                    <td colSpan={colunas.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
                      Nenhum registro encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="proposta" />
        </div>
      </div>
      </>)}

      <input ref={pdfPropostasInputRef} type="file" accept="application/pdf,.pdf" multiple onChange={selecionarPropostas} style={{ display: 'none' }} />
      <input ref={pdfApoliceInputRef} type="file" accept="application/pdf,.pdf" onChange={importarPdfApolice} style={{ display: 'none' }} />
      <input ref={pdfApolicesInputRef} type="file" accept="application/pdf,.pdf" multiple onChange={selecionarApolicesLote} style={{ display: 'none' }} />
      {importacaoLote && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 360, background: 'rgba(0,0,0,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 'min(1040px, 100%)', maxHeight: '88vh', background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 18px 48px rgba(0,0,0,0.24)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: '0 0 5px', fontSize: 19 }}>Importar propostas</h3>
                <div style={{ color: '#666', fontSize: 13 }}>
                  Confira os dados encontrados e escolha quais propostas deseja cadastrar.
                  {importacaoLote.excedentes > 0 && ` ${importacaoLote.excedentes} arquivo(s) excederam o limite de 30 e não foram incluídos.`}
                </div>
              </div>
              <button type="button" aria-label="Fechar" disabled={salvandoLote} onClick={() => setImportacaoLote(null)} style={{ border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            <div style={{ overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 10, minHeight: 180 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#F8F9FA', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 38, padding: 10 }}></th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Arquivo</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Cliente</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Seguradora</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Nº da proposta</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Item</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {importacaoLote.itens.map(item => {
                    const podeSelecionar = item.status === 'pronta'
                    const situacoes = {
                      aguardando: ['Aguardando', '#6B7280'], lendo: ['Lendo PDF...', '#B45309'], pronta: ['Pronta', '#15803D'],
                      duplicada: ['Duplicada', '#B45309'], erro: ['Erro na leitura', '#B91C1C'], cadastrando: ['Cadastrando...', '#1D4ED8'],
                      cadastrada: ['Cadastrada', '#15803D'], erro_cadastro: ['Erro no cadastro', '#B91C1C'],
                      endosso_sugerido: ['Parece Endosso', '#B45309'], endosso_sem_apolice: ['Endosso sem apólice', '#B91C1C']
                    }
                    const situacao = situacoes[item.status] || [item.status, '#555']
                    const mostrarEscolha = item.status === 'endosso_sugerido' && !item.endossoExpandido
                    const mostrarFormEndosso = item.status === 'endosso_sugerido' && item.endossoExpandido
                    return (
                      <Fragment key={item.id}>
                        <tr style={{ borderTop: '1px solid #EEF0F2', background: item.status === 'cadastrada' ? '#F2FBF5' : '#fff' }}>
                          <td style={{ padding: 10, textAlign: 'center' }}>
                            <input type="checkbox" checked={!!item.selecionado} disabled={!podeSelecionar || salvandoLote} onChange={e => atualizarItemLote(item.id, { selecionado: e.target.checked })} />
                          </td>
                          <td style={{ padding: 10, maxWidth: 190, wordBreak: 'break-word' }}>{item.nome}</td>
                          <td style={{ padding: 10 }}>{item.dados?.cliente_nome || '—'}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{item.dados?.seguradora || '—'}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{item.dados?.numero_proposta || '—'}</td>
                          <td style={{ padding: 10 }}>
                            {(() => {
                              const d = item.dados || {}
                              // Veiculo: modelo/placa. Residencial/Empresarial/Condominio: tipo ou
                              // atividade + endereco do local de risco. Outros ramos: o proprio ramo.
                              const endereco = [d.res_endereco, d.res_numero].filter(Boolean).join(', ')
                              const local = [d.res_tipo || d.res_atividade, endereco].filter(Boolean).join(' — ')
                              const principal = d.item_modelo || d.item_placa || local || d.ramo || '—'
                              const sub = d.item_modelo && d.item_placa ? d.item_placa : (local && d.ramo ? d.ramo : '')
                              return (<>
                                <div>{principal}</div>
                                {sub && <div style={{ color: '#777', fontSize: 11 }}>{sub}</div>}
                              </>)
                            })()}
                            {item.dados?.itens?.length > 1 && <div style={{ color: '#777', fontSize: 11 }}>+ {item.dados.itens.length - 1} item(ns) — {item.dados.itens.length} no total</div>}
                          </td>
                          <td style={{ padding: 10, textAlign: 'center', minWidth: 130 }}>
                            <span style={{ color: situacao[1], fontWeight: 700 }}>{situacao[0]}</span>
                            {mostrarEscolha && (
                              <div style={{ marginTop: 4, color: '#777', fontSize: 10.5 }}>
                                Apólice nº {item.endossoInfo?.numero_apolice || '—'} já cadastrada com a mesma vigência final.
                                <div style={{ marginTop: 4, fontWeight: 600, color: '#555' }}>É um endosso?</div>
                                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 4 }}>
                                  <button type="button" className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 10.5 }}
                                    onClick={() => atualizarItemLote(item.id, { endossoExpandido: true })}>
                                    Sim
                                  </button>
                                  <button type="button" className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 10.5 }}
                                    onClick={() => recusarEndossoLote(item)}>
                                    Não
                                  </button>
                                  <button type="button" className="btn btn-secondary" title="Excluir" style={{ width: 20, height: 20, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => excluirItemLote(item.id)}>
                                    <IconExcluir size={11} />
                                  </button>
                                </div>
                              </div>
                            )}
                            {item.erro && <div title={item.erro} style={{ marginTop: 3, color: '#777', fontSize: 10, maxWidth: 180 }}>{item.erro}</div>}
                          </td>
                        </tr>
                        {mostrarFormEndosso && (
                          <tr style={{ background: '#FFF8EE' }}>
                            <td colSpan={7} style={{ padding: '10px 14px 14px' }}>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div style={{ minWidth: 220 }}>
                                  <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 3 }}>Tipo de Endosso *</label>
                                  <select className="form-select" style={{ fontSize: 12 }} value={item.endossoTipo || ''}
                                    onChange={e => atualizarItemLote(item.id, { endossoTipo: e.target.value })}>
                                    <option value="">Selecione...</option>
                                    {tiposEndosso.map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                                <div style={{ flex: 1, minWidth: 260 }}>
                                  <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 3 }}>Observação</label>
                                  <textarea className="form-input" rows={1} style={{ fontSize: 12, resize: 'vertical' }} value={item.endossoObs || ''}
                                    onChange={e => atualizarItemLote(item.id, { endossoObs: e.target.value })} />
                                </div>
                                <div style={{ display: 'flex', gap: 6, alignSelf: 'flex-end', paddingBottom: 2 }}>
                                  <button type="button" className="btn btn-secondary" style={{ padding: '5px 10px', fontSize: 12 }}
                                    onClick={() => atualizarItemLote(item.id, { endossoExpandido: false })}>
                                    Voltar
                                  </button>
                                  <button type="button" className="btn btn-primary" style={{ padding: '5px 10px', fontSize: 12 }}
                                    disabled={!item.endossoTipo} onClick={() => confirmarEndossoLote(item)}>
                                    Cadastrar Endosso
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 18 }}>
              <div style={{ color: '#666', fontSize: 12 }}>
                {lendoLote
                  ? 'Os PDFs estão sendo lidos um por vez.'
                  : `${importacaoLote.itens.filter(i => i.status === 'cadastrada').length} cadastrada(s) · ${importacaoLote.itens.filter(i => i.selecionado && i.status === 'pronta').length} selecionada(s)`}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" disabled={salvandoLote} onClick={() => setImportacaoLote(null)}>Sair</button>
                <button type="button" className="btn btn-primary" disabled={lendoLote || salvandoLote || !importacaoLote.itens.some(i => i.selecionado && i.status === 'pronta')} onClick={cadastrarLote}>
                  {salvandoLote ? 'Cadastrando...' : `Cadastrar ${importacaoLote.itens.filter(i => i.selecionado && i.status === 'pronta').length} proposta(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {importacaoLoteApolice && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 360, background: 'rgba(0,0,0,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 'min(1040px, 100%)', maxHeight: '88vh', background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 18px 48px rgba(0,0,0,0.24)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: '0 0 5px', fontSize: 19 }}>Importar apólices</h3>
                <div style={{ color: '#666', fontSize: 13 }}>
                  Cada PDF é casado com a proposta pendente correspondente pelo número da proposta ou pela placa. Confira (ou escolha manualmente) antes de cadastrar.
                  {importacaoLoteApolice.excedentes > 0 && ` ${importacaoLoteApolice.excedentes} arquivo(s) excederam o limite de 30 e não foram incluídos.`}
                </div>
              </div>
              <button type="button" aria-label="Fechar" disabled={salvandoLoteApolice} onClick={() => setImportacaoLoteApolice(null)} style={{ border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            <div style={{ overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 10, minHeight: 180 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#F8F9FA', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 38, padding: 10 }}></th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Arquivo</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Proposta correspondente</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Nº apólice/endosso</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {importacaoLoteApolice.itens.map(item => {
                    const podeSelecionar = item.status === 'pronta'
                    const situacoes = {
                      aguardando: ['Aguardando', '#6B7280'], lendo: ['Lendo PDF...', '#B45309'], pronta: ['Pronta', '#15803D'],
                      sem_correspondencia: ['Sem correspondência', '#B45309'], erro: ['Erro na leitura', '#B91C1C'],
                      cadastrando: ['Importando...', '#1D4ED8'], cadastrada: ['Importada', '#15803D'], erro_cadastro: ['Erro ao importar', '#B91C1C']
                    }
                    const situacao = situacoes[item.status] || [item.status, '#555']
                    return (
                      <tr key={item.id} style={{ borderTop: '1px solid #EEF0F2', background: item.status === 'cadastrada' ? '#F2FBF5' : '#fff' }}>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!item.selecionado} disabled={!podeSelecionar || salvandoLoteApolice} onChange={e => atualizarItemLoteApolice(item.id, { selecionado: e.target.checked })} />
                        </td>
                        <td style={{ padding: 10, maxWidth: 190, wordBreak: 'break-word' }}>{item.nome}</td>
                        <td style={{ padding: 10, minWidth: 220 }}>
                          {item.proposta ? (
                            <>{item.proposta.cliente_nome} <span style={{ color: '#888' }}>· {item.proposta.seguradora} · nº {item.proposta.numero_proposta || 's/nº'}</span></>
                          ) : item.status === 'sem_correspondencia' ? (
                            <select className="form-input" style={{ fontSize: 12, padding: '4px 6px' }} value=""
                              onChange={e => escolherPropostaLoteApolice(item.id, e.target.value)} disabled={salvandoLoteApolice}>
                              <option value="">Selecione a proposta...</option>
                              {porAba.pendente.map(p => (
                                <option key={p.id} value={p.id}>{p.cliente_nome} · {p.seguradora} · nº {p.numero_proposta || 's/nº'}</option>
                              ))}
                            </select>
                          ) : '—'}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center' }}>{item.extraido?.numero_apolice || item.extraido?.numero_endosso || '—'}</td>
                        <td style={{ padding: 10, textAlign: 'center', minWidth: 150 }}>
                          <span style={{ color: situacao[1], fontWeight: 700 }}>{situacao[0]}</span>
                          {item.erro && <div title={item.erro} style={{ marginTop: 3, color: '#777', fontSize: 10, maxWidth: 180 }}>{item.erro}</div>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 18 }}>
              <div style={{ color: '#666', fontSize: 12 }}>
                {lendoLoteApolice
                  ? 'Os PDFs estão sendo lidos um por vez.'
                  : `${importacaoLoteApolice.itens.filter(i => i.status === 'cadastrada').length} importada(s) · ${importacaoLoteApolice.itens.filter(i => i.selecionado && i.status === 'pronta').length} selecionada(s)`}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" disabled={salvandoLoteApolice} onClick={() => setImportacaoLoteApolice(null)}>Sair</button>
                <button type="button" className="btn btn-primary" disabled={lendoLoteApolice || salvandoLoteApolice || !importacaoLoteApolice.itens.some(i => i.selecionado && i.status === 'pronta')} onClick={cadastrarLoteApolice}>
                  {salvandoLoteApolice ? 'Importando...' : `Importar ${importacaoLoteApolice.itens.filter(i => i.selecionado && i.status === 'pronta').length} apólice(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {escolhaEmissao && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 350, background: 'rgba(0,0,0,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 'min(520px, 100%)', background: '#fff', borderRadius: 14, padding: 26, boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 19 }}>Como deseja emitir?</h3>
            <div style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
              {escolhaEmissao.cliente_nome || 'Proposta'} · nº {escolhaEmissao.numero_proposta || 'não informado'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button type="button" disabled={importandoApolice} onClick={() => pdfApoliceInputRef.current?.click()} style={{
                padding: 18, border: '1px solid var(--primary)', borderRadius: 10, background: '#FFF7F3', color: '#9A2A00',
                cursor: importandoApolice ? 'wait' : 'pointer', textAlign: 'left', fontFamily: 'inherit'
              }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>{importandoApolice ? 'Lendo apólice...' : 'Enviar PDF da apólice'}</strong>
                <span style={{ fontSize: 12, lineHeight: 1.45 }}>Lê, atualiza os dados encontrados e anexa o PDF automaticamente.</span>
              </button>
              <button type="button" disabled={importandoApolice} onClick={() => {
                const proposta = escolhaEmissao
                setEscolhaEmissao(null)
                setReabrindo(true)
                editarProposta(proposta, true).finally(() => setReabrindo(false))
              }} style={{
                padding: 18, border: '1px solid #D9DDE3', borderRadius: 10, background: '#fff', color: '#333',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
              }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Emitir manualmente</strong>
                <span style={{ fontSize: 12, lineHeight: 1.45 }}>Abre Dados de Emissão e posiciona o cursor no número da apólice.</span>
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" disabled={importandoApolice} onClick={() => setEscolhaEmissao(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {resultadoImportacao && (
        <div className="modal-overlay" style={{ zIndex: 400 }} onClick={e => {
          if (e.target === e.currentTarget) setResultadoImportacao(null)
        }}>
          <div className="modal" style={{ maxWidth: 680, width: 'min(680px, 94vw)' }}>
            <div className="modal-header">
              <span className="modal-title">{resultadoImportacao.titulo}</span>
              <button className="modal-close" type="button" onClick={() => setResultadoImportacao(null)}>×</button>
            </div>
            <div style={{ padding: '20px 24px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: resultadoImportacao.alteracoes ? 18 : 4 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flex: '0 0 34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: resultadoImportacao.tipo === 'sucesso' ? '#EAF7EE' : '#FDECEC',
                  color: resultadoImportacao.tipo === 'sucesso' ? '#218838' : '#C62828', fontWeight: 800, fontSize: 18
                }}>{resultadoImportacao.tipo === 'sucesso' ? '✓' : '!'}</div>
                <div>
                  <div style={{ color: '#333', fontSize: 14, lineHeight: 1.5 }}>{resultadoImportacao.mensagem}</div>
                  {resultadoImportacao.tipo === 'sucesso' && resultadoImportacao.alteracoes?.length === 0 && (
                    <div style={{ color: '#777', fontSize: 13, marginTop: 5 }}>Os dados encontrados são iguais aos que já estavam cadastrados.</div>
                  )}
                </div>
              </div>

              {resultadoImportacao.alteracoes?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Dados atualizados</div>
                  <div style={{ border: '1px solid #E4E7EB', borderRadius: 9, overflow: 'auto', maxHeight: '48vh' }}>
                    {resultadoImportacao.alteracoes.map((alteracao, indice) => (
                      <div key={`${alteracao.campo}-${indice}`} style={{
                        padding: '11px 13px', borderBottom: indice < resultadoImportacao.alteracoes.length - 1 ? '1px solid #EEF0F2' : 'none',
                        display: 'grid', gridTemplateColumns: '155px minmax(0, 1fr)', gap: 12, fontSize: 12
                      }}>
                        <strong style={{ color: '#333' }}>{alteracao.campo}</strong>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: '#999', wordBreak: 'break-word' }}>{alteracao.anterior || 'não preenchido'}</div>
                          <div style={{ color: '#218838', fontWeight: 600, marginTop: 3, wordBreak: 'break-word' }}>→ {alteracao.novo || 'não preenchido'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                {resultadoImportacao.tipo === 'sucesso' && resultadoImportacao.documento && (
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    const documento = resultadoImportacao.documento
                    setResultadoImportacao(null)
                    setReabrindo(true)
                    editarProposta(documento, true).finally(() => setReabrindo(false))
                  }}>Ver Apólice</button>
                )}
                <button type="button" className="btn btn-primary" onClick={() => setResultadoImportacao(null)}>Sair</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {documentoModal && (
        <Apolices
          modalOnly
          hideNavigation
          initialDocument={documentoModal.initialDocument || null}
          initialFormOverrides={documentoModal.initialFormOverrides || null}
          focarEmissao={!!documentoModal.focarEmissao}
          focarEnvio={!!documentoModal.focarEnvio}
          onCloseModal={() => setDocumentoModal(null)}
          onSaved={async () => {
            setDocumentoModal(null)
            await carregar()
          }}
        />
      )}
    </>
  )
}
